'use client';

import { forwardRef } from 'react';
import type { AutocompleteProps as MantineAutocompleteProps } from '@mantine/core';
import { Autocomplete as MantineAutocomplete } from '@mantine/core';

import type { LockedProps } from '../types';

export type AutocompleteProps = Omit<MantineAutocompleteProps, LockedProps>;

/**
 * ### Autocomplete
 *
 * @description
 * Mantine's Autocomplete minus per-call-site style overrides (see {@link LockedProps}). A free-text
 * input with suggestions: unlike `Select`, the value is whatever the user types — `data` only
 * offers completions, it doesn't constrain the value.
 *
 * Reach for `Select` when the value must be one of the options, and `MultiSelect` when more than
 * one may be picked.
 *
 * @example
 * ```tsx
 * <Autocomplete
 *   aria-label="Filter by owner"
 *   placeholder="Filter by owner"
 *   data={owners}
 *   value={filter}
 *   onChange={setFilter}
 * />
 * ```
 */
export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>((props, ref) => {
  return <MantineAutocomplete ref={ref} {...props} />;
});

Autocomplete.displayName = 'Autocomplete';
